import { cleanLayers, type ScriptLayers } from './layers';
import { cleanParams, type ScriptParams } from './params';
import { cleanSettings, type ScriptSettings } from './settings';

/** What a drawing file calls itself, so any other JSON is refused. */
const FORMAT = 'draw';

/** The version this code writes; a file names the version it was written in. */
export const DRAWING_FILE_VERSION = 1;

export const DRAWING_FILE_EXTENSION = 'draw.json';
export const DRAWING_FILE_MIME = 'application/json';

/** Everything needed to rebuild a drawing. */
export interface DrawingFile {
  readonly name: string;
  readonly source: string;
  readonly params: ScriptParams;
  readonly layers: ScriptLayers;
  readonly settings: ScriptSettings;
}

export type DrawingFileRead =
  | { readonly ok: true; readonly drawing: DrawingFile }
  | { readonly ok: false; readonly problem: string };

type Fields = Record<string, unknown>;

/**
 * Turns a file of each older version into the next one, keyed by the version it reads, so a
 * change to the file adds a step here and every older file still reads.
 */
const UPGRADES: Readonly<Record<number, (fields: Fields) => Fields>> = {};

export function writeDrawingFile(drawing: DrawingFile): string {
  const { name, source, params, layers, settings } = drawing;
  const file = {
    format: FORMAT,
    version: DRAWING_FILE_VERSION,
    name,
    source,
    params,
    layers,
    settings,
  };
  return `${JSON.stringify(file, null, 2)}\n`;
}

/**
 * The drawing a file holds, or why it cannot be read. The file's shape is checked strictly;
 * entries inside its params and layers are cleaned as a client's would be.
 */
export function readDrawingFile(text: string): DrawingFileRead {
  const refuse = (problem: string): DrawingFileRead => ({ ok: false, problem });
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return refuse('The file is not a drawing file: it is not valid JSON');
  }
  if (typeof parsed !== 'object' || parsed === null || (parsed as Fields)['format'] !== FORMAT) {
    return refuse('The file is not a drawing file');
  }
  let fields = parsed as Fields;
  const version = fields['version'];
  if (typeof version !== 'number' || !Number.isInteger(version) || version < 1) {
    return refuse('The drawing file has no valid version');
  }
  if (version > DRAWING_FILE_VERSION) {
    return refuse(
      `The drawing file is version ${version}, from a newer draw; this one reads up to version ${DRAWING_FILE_VERSION}`,
    );
  }
  for (let at = version; at < DRAWING_FILE_VERSION; at += 1) fields = UPGRADES[at]!(fields);

  const { name, source, params, layers, settings } = fields;
  if (typeof name !== 'string' || name.trim() === '') return refuse('The drawing file has no name');
  if (typeof source !== 'string') return refuse('The drawing file has no script');
  if (!Array.isArray(params)) return refuse('The drawing file has params that are not a list');
  if (!Array.isArray(layers)) return refuse('The drawing file has layers that are not a list');
  if (typeof settings !== 'object' || settings === null || Array.isArray(settings)) {
    return refuse('The drawing file has settings that are not an object');
  }
  return {
    ok: true,
    drawing: {
      name: name.trim(),
      source,
      params: cleanParams(params),
      layers: cleanLayers(layers),
      settings: cleanSettings(settings),
    },
  };
}

/** `name`, or, when it is taken, its first free numbered form: `Plate (2)`, `Plate (3)`... */
export function uniqueName(name: string, taken: readonly string[]): string {
  const used = new Set(taken);
  if (!used.has(name)) return name;
  const base = name.replace(/ \(\d+\)$/, '');
  let n = 2;
  while (used.has(`${base} (${n})`)) n += 1;
  return `${base} (${n})`;
}
